import { useMemo } from 'react';
import { useWatch, type Control, type FieldValues } from 'react-hook-form';
import type { Reaction, ReactionOverrides } from './reactions';

const EMPTY_OVERRIDES: ReactionOverrides = {};
const EMPTY_REACTIONS: Reaction[] = [];

function getDefaultTransformForTarget(target: string): (value: unknown) => unknown {
  switch (target) {
    case 'disabled':
      return (v) => !v;
    case 'visible':
    case 'enabled':
      return (v) => Boolean(v);
    case 'required':
      return (v) => Boolean(v);
    default:
      return (v) => v;
  }
}

/**
 * Evaluates an array of Reaction declarations against live form values.
 *
 * Makes a single useWatch call for all watched fields, then runs each reaction's
 * transform to produce a flat overrides object. Adapters merge these overrides
 * with their static props (reactions take precedence).
 *
 * @returns ReactionOverrides — computed prop values keyed by ReactionTarget.
 *   When no reactions are provided, returns a stable empty object (no re-renders).
 */
export function useReactions(
  control: Control<FieldValues>,
  reactions: Reaction[] | undefined,
): ReactionOverrides {
  const activeReactions = reactions ?? EMPTY_REACTIONS;

  const watchedFieldNames = useMemo(() => {
    if (activeReactions.length === 0) return [] as string[];
    const names = new Set<string>();
    for (const r of activeReactions) {
      if (Array.isArray(r.watch)) {
        for (const w of r.watch) names.add(w);
      } else {
        names.add(r.watch);
      }
    }
    return Array.from(names);
  }, [activeReactions]);

  const watchedValues = useWatch({
    control,
    name: watchedFieldNames.length > 0 ? watchedFieldNames : (undefined as any),
    disabled: watchedFieldNames.length === 0,
  });

  return useMemo(() => {
    if (activeReactions.length === 0) return EMPTY_OVERRIDES;

    const valueMap: Record<string, unknown> = {};
    if (watchedFieldNames.length === 1) {
      valueMap[watchedFieldNames[0]] = Array.isArray(watchedValues)
        ? watchedValues[0]
        : watchedValues;
    } else if (Array.isArray(watchedValues)) {
      watchedFieldNames.forEach((name, i) => {
        valueMap[name] = watchedValues[i];
      });
    }

    const overrides: Record<string, unknown> = {};

    for (const reaction of activeReactions) {
      const transform = reaction.transform ?? getDefaultTransformForTarget(reaction.set);

      let input: unknown;
      if (Array.isArray(reaction.watch)) {
        const multiValues: Record<string, unknown> = {};
        for (const fieldName of reaction.watch) {
          multiValues[fieldName] = valueMap[fieldName];
        }
        input = multiValues;
      } else {
        input = valueMap[reaction.watch];
      }

      overrides[reaction.set] = transform(input);
    }

    return overrides as ReactionOverrides;
  }, [activeReactions, watchedFieldNames, watchedValues]);
}
